import React, { useState, useEffect } from 'react';
import BundleItem from './BundleItem';
import FormatPrice from './FormatPrice';
import BundleSkeleton from '../../templates/components/common/BundleSkeleton';
import { AlgoliaProductHit } from '../../utils/types';
import { hasReachedMaxQty } from '../../utils/checkMaxQty/hasReachedMaxQty';
import { showMaxQtyAlert } from '../../utils/checkMaxQty/showMaxQtyAlert';
import { submitTracking } from '@jbhi-fi/components/src/Tracking/utils/shopifyActionTrackingHelper';
import { trackAddToCart } from '@jbhi-fi/utils/src/ga4Tracking';
import { getStoreCurrency } from '../../utils/shopify/getStoreCurrency';
import { transformAlgoliaHitForTracking } from '@jbhi-fi/components/src/Algolia/transformers/transformAlgoliaHitForTracking';
import CalloutFont from '@jbhi-fi/lanyard-ui/font/CalloutFont';
import { ActionButton } from '@jbhi-fi/components/src/ActionButton';
import { CartActionState } from '@jbhi-fi/components/src/CartProvider/types';
import { ADD_TO_CART_CONFIRMATION_DURATION_MS } from '../../templates/components/pdp/callToAction/AddToCart';
import { filterAndMapAttributes } from '@jbhi-fi/apis/src/utils/filterAndMapAttributes';
import { ShopifyStorefrontClient } from '../../api/shopify/ShopifyStorefrontClient';
import { styles } from './BundleContainer.css';
import { JbCartItem } from '@jbhi-fi/apis/src/shopify/types/JbCart';
import { updateCart } from '../../utils/cart/cartUtils';

type CheckboxState = {
  itemSku: string;
  checked: boolean;
};

export type BundleContainerProps = {
  mainProductSku: string | number;
  products?: Array<AlgoliaProductHit>;
  showPrice: boolean;
  isLoading?: boolean;
  cartItems: Array<JbCartItem>;
};

const storefrontClient = new ShopifyStorefrontClient();

const BundleContainer = ({
  products = undefined,
  showPrice = false,
  isLoading = false,
  cartItems,
}: BundleContainerProps) => {
  const [buttonState, setButtonState] = useState<CartActionState>(CartActionState.Default);
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const [checkboxStates, setCheckboxStates] = useState<Array<CheckboxState>>([]);
  const [totalBundlePrice, setTotalBundlePrice] = useState<number>(0);
  const [productsSelected, setProductsSelected] = useState<Array<any>>([{}]);
  const gtmData: Array<any> = [];

  const themeProduct = window.Product;

  const sortMainProductSkuFirst = (product: AlgoliaProductHit) => (product.sku === themeProduct?.sku ? -1 : 1);

  useEffect(() => {
    if (products !== null) {
      // Gets state of the bundle checkboxes and creates an array of objects with the sku and checked state
      const productCheckboxes = products?.map(product => {
        return { itemSku: product.sku, checked: true };
      });
      setCheckboxStates(productCheckboxes || []);
    }

    const impressions = products?.map((product, index) => ({
      ...product.google_tag_manager,
      list: 'recommendations-pdp-GIAT',
      position: index,
    }));

    window.dataLayer.push({
      event: 'productImpression',
      group: 'shopifyBundle',
      category: 'getItAllTogether',
      pageType: 'pdp',
      ecommerce: {
        impressions: impressions,
      },
    });
  }, [products]);

  useEffect(() => {
    // Uses the checkboxStates state array to determine if the bundle item selected and update if changed accordingly
    if (checkboxStates) {
      const selectedProductSkus = checkboxStates.filter(checkbox => checkbox.checked === true);
      let totalPrice = 0;
      const selectedProductArray: Array<AlgoliaProductHit> = [];
      selectedProductSkus.forEach(product => {
        const productSelected = products?.filter(p => p.sku === product.itemSku) || [];
        const productPrice = products?.find(p => p.sku === product.itemSku)?.price;
        selectedProductArray.push(...productSelected);
        if (productPrice) totalPrice += productPrice;
      });
      setTotalBundlePrice(totalPrice);
      setProductsSelected(selectedProductArray);
    }
  }, [checkboxStates, products]);

  const updateCheckboxState = (sku: string, checked: boolean): void => {
    const newCheckboxStates = checkboxStates.map(c => (c.itemSku === sku ? { ...c, checked } : c));
    setCheckboxStates(newCheckboxStates);
  };

  const addToCartProductData = productsSelected.map(product => {
    gtmData.push(product.google_tag_manager);
    return {
      id: product.variant_id,
      sku: product.sku,
      quantity: 1,
      properties: {
        _availabilityCanPreOrder: product.availability?.canPreOrder,
        _availabilityFulfilment: product.availability?.fulfilment,
        _maxQty: product.availability?.fulfilment === 'Digital' ? 1 : product.product?.limitPerOrder,
        _priceAtc: Math.round(product.price * 100),
      },
    };
  });
  const handleGtmEvent = () => {
    submitTracking({
      event: 'addToCart',
      trackingDescriptor: {
        page: 'pdp',
        group: 'shopifyBundle',
        category: 'getItAllTogether',
        currency: getStoreCurrency(),
        ecommerceExtraProps: {
          list: 'recommendations-pdp-GIAT',
        },
      },
      gtm: { gtmData },
    });

    trackAddToCart({
      location: 'pdp',
      currency: getStoreCurrency(),
      addedItems: transformAlgoliaHitForTracking(products || [], { listName: 'recommendations-pdp-GIAT' }),
    });
  };
  const handleClick = async () => {
    setIsButtonLoading(true);
    if (cartItems.length > 0) {
      const unAddableItem = addToCartProductData.find(item => hasReachedMaxQty(item.id));

      if (unAddableItem) {
        showMaxQtyAlert();
        setButtonState(CartActionState.Default);
        setIsButtonLoading(false);
        return;
      }
    }

    const cart = await storefrontClient.addMultipleToCart(
      addToCartProductData.map(item => {
        return {
          variantId: item.id,
          quantity: 1,
          attributes: filterAndMapAttributes(item.properties),
        };
      })
    );

    handleGtmEvent();
    await updateCart(cart);
    setButtonState(CartActionState.Complete);
    setIsButtonLoading(false);
  };

  useEffect(() => {
    if (buttonState !== CartActionState.Complete) return;
    let shouldCancel = false;

    setTimeout(() => {
      if (!shouldCancel) setButtonState(CartActionState.Default);
    }, ADD_TO_CART_CONFIRMATION_DURATION_MS);

    return () => {
      shouldCancel = true;
    };
  }, [buttonState]);

  return (
    <div>
      <div className={styles.bundleContent}>
        <CalloutFont as="h4" className={styles.bundleHeading} testId="bundle-heading">
          {themeProduct.bundleStrings.bundleHeading}
        </CalloutFont>
        {!isLoading && products?.length ? (
          <div className={styles.bundleInner}>
            <div className={styles.bundleItems}>
              {[...products].sort(sortMainProductSkuFirst).map((product, index) => {
                return (
                  <BundleItem
                    key={product.sku}
                    index={index}
                    product={product}
                    isChecked={!!checkboxStates.find(c => c.itemSku === product.sku)?.checked}
                    onCheck={updateCheckboxState}
                    shouldShowPrice={showPrice}
                    shouldShowCheckbox={index !== 0 && products.length > 2}
                    trackingDescriptor={{
                      currency: getStoreCurrency(),
                      page: 'pdp',
                      category: 'getItAllTogether',
                      group: 'shopifyBundle',
                      ecommerceExtraProps: {
                        list: 'recommendations-pdp-GIAT',
                      },
                    }}
                  />
                );
              })}
            </div>
            <div className={styles.bundleFooter}>
              <div>
                <div className={styles.bundlePricing}>
                  <div className={styles.priceTag}>
                    <sup>$</sup>
                    <FormatPrice priceToFormat={totalBundlePrice} />
                  </div>
                </div>
              </div>
              <div className={styles.bundleCta}>
                <ActionButton
                  label={
                    productsSelected.length >= products.length
                      ? themeProduct.bundleStrings.bundleCTA
                      : 'Add items to cart'
                  }
                  onClick={handleClick}
                  isLoading={isButtonLoading}
                  state={buttonState}
                  actionFinishedLabel="Added!"
                />
              </div>
            </div>
          </div>
        ) : (
          <BundleSkeleton loading />
        )}
      </div>
    </div>
  );
};

export default BundleContainer;
